/**
 * VH-248: small pure helpers for the phone nutrition page.
 */

/** The user's local calendar day as YYYY-MM-DD — never `toISOString()`, which is UTC and
 *  turns a late Zurich evening into tomorrow. */
export function localIsoDate(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** `days` before or after a YYYY-MM-DD day, in local calendar days (never through UTC). */
export function shiftIsoDate(iso, days) {
  const [y, m, d] = iso.split('-').map(Number)
  return localIsoDate(new Date(y, m - 1, d + days))
}

/** The moment a meal is logged at: the day shown, at the clock time of logging — so a meal
 *  added to yesterday is not stamped with today's date. */
export function mealTimeOn(iso, now = new Date()) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds()).toISOString()
}

/** A sensible default meal for the clock. Shown as a pre-selected chip, one tap to change. */
export function mealTypeForHour(hour) {
  if (hour < 10) return 'breakfast'
  if (hour < 14) return 'lunch'
  if (hour < 17) return 'snack'
  if (hour < 22) return 'dinner'
  return 'snack'
}

const SLOT_ORDER = ['breakfast', 'lunch', 'afternoon', 'dinner', 'dessert']

/** The first planned slot of today that is not logged yet, or null. `training` is an addition,
 *  not a meal of the day, so it is never "next". */
export function nextPlannedMeal(plan, date) {
  if (!plan?.meals?.length) return null
  const logged = new Set((plan.logged ?? []).filter((e) => e.date === date).map((e) => e.slot))
  const meals = [...plan.meals]
    .filter((m) => m.slot !== 'training' && !logged.has(m.slot))
    .sort((a, b) => SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot))
  return meals[0] ?? null
}

/** The plan that covers `date`, as PlanMealLogger picks it. */
export function planForDate(plans, date) {
  const list = Array.isArray(plans) ? plans : []
  return list.find((p) => p.week_start <= date && date <= p.week_end) ?? null
}

/** Sum of today's meals. The meals list is what the page shows, so the totals cannot disagree
 *  with it. */
export function totalsOf(meals) {
  return (meals ?? []).reduce(
    (sum, m) => ({
      kcal: sum.kcal + (Number(m.total_calories) || 0),
      protein_g: sum.protein_g + (Number(m.total_protein_g) || 0),
      carbs_g: sum.carbs_g + (Number(m.total_carbs_g) || 0),
      fat_g: sum.fat_g + (Number(m.total_fat_g) || 0),
    }),
    { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
  )
}

/** What a meal is, in words: its foods, not "Lunch at 12:30". */
export function mealLabel(meal) {
  const names = (meal?.meal_foods ?? []).map((f) => f.name || f.food?.name).filter(Boolean)
  return names.length ? names.join(', ') : meal?.name || ''
}
